<template>
    <jet-form-section @submitted="updateProfileInformation">
        <template #title> Profile Information </template>

        <template #description> Update your account's profile information and email address. </template>

        <template #form>
            <!-- Profile Photo -->
            <div v-if="$page.props.jetstream.managesProfilePhotos" class="col-span-6 sm:col-span-4">
                <!-- Profile Photo File Input -->
                <input type="file" class="hidden" ref="photo" @change="updatePhotoPreview" />

                <jet-label for="photo" value="Photo" />

                <div class="flex mt-2 space-x-6">
                    <!--Profile Photo-->
                    <avatar :user="user" class="w-20 h-20" />

                    <div class="flex items-center space-x-3">
                        <jet-secondary-button type="button" @click.native.prevent="selectNewPhoto"> Change </jet-secondary-button>
                        <jet-button ref="saveChangesButton" v-show="photoPreview" size="sm" color="indigo" :processing="form.processing" type="submit">Save Changes?</jet-button>
                    </div>

                    <div v-show="user.profilePhotoPath" class="flex items-center">
                        <button class="text-red-500 hover:text-red-700" @click.prevent="deletePhoto">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                </div>

                <jet-input-error :message="form.errors.photo" class="mt-2" />
            </div>

            <!-- Username -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="uname" value="Username" />
                <!--<jet-input id="uname" v-model="form.username" class="block mt-1 w-full" type="text" />-->
                <input-username id="uname" v-model="form.username" class="block mt-1 w-full" type="text" autocomplete="off" data-lpignore="true" />
                <jet-input-error :message="form.errors.username" class="mt-2" />
            </div>

            <!-- Name -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="name" value="Name" />
                <jet-input id="name" type="text" class="block mt-1 w-full" v-model="form.name" autocomplete="off" data-lpignore="true" />
                <jet-input-error :message="form.errors.name" class="mt-2" />
            </div>

            <!-- Email -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="userEmail" value="Email" />
                <jet-input id="userEmail" type="email" class="block mt-1 w-full" v-model="form.userEmail" />
                <jet-input-error :message="form.errors.email" class="mt-2" />
            </div>

            <!-- Phone -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="phone" value="Mobile Number" />
                <jet-input id="phone" type="tel" class="block mt-1 w-full" v-model="form.phone" :country-code.sync="form.phone_country" />
                <jet-input-error :message="form.errors.phone" class="mt-2" />
            </div>

            <!--Birthdate-->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="birthdate" value="Birthdate" />
                <!--<jet-input id="birthdate" type="text" readonly="readonly" :disabled="true" class="block mt-1 w-full" v-model="form.birthdate" />-->
                <div class="block mt-1 w-full">{{ formatBirthdate(user.birthdate) }}</div>
            </div>
        </template>

        <template #actions>
            <jet-action-message :show="form.recentlySuccessful" class="mr-3">
                <div class="flex items-center space-x-1">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Saved</span>
                </div>
            </jet-action-message>

            <jet-button :processing="form.processing"> Save </jet-button>
        </template>
    </jet-form-section>
</template>

<script>
import JetButton from "@/Jetstream/Button"
import JetFormSection from "@/Jetstream/FormSection"
import JetInput from "@/Jetstream/Input"
import JetInputError from "@/Jetstream/InputError"
import JetLabel from "@/Jetstream/Label"
import JetActionMessage from "@/Jetstream/ActionMessage"
import JetSecondaryButton from "@/Jetstream/SecondaryButton"
import InputUsername from "@/Components/Forms/InputUsername"

export default {
    components: {
        InputUsername,
        JetActionMessage,
        JetButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
    },

    data() {
        return {
            user: this.$page.props.user,
            form: this.$inertia.form({
                _method: "PUT",
                name: this.$page.props.user.name,
                userEmail: this.$page.props.user.email,
                username: this.$page.props.user.username,
                phone: this.$page.props.user.phoneHumanReadable,
                phone_country: this.$page.props.user.phoneCountry,
                photo: null,
            }),
            photoPreview: null,
        }
    },

    methods: {
        formatBirthdate(date) {
            // For birthdates, use the UTC date directly without converting to local time
            return moment.utc(date).format("MMMM DD, YYYY")
        },
        updateProfileInformation() {
            if (this.$refs.photo) {
                this.form.photo = this.$refs.photo.files[0]
            }

            this.form
                .transform((data) => ({
                    ...data,
                    email: data.userEmail,
                }))
                .post(route("user-profile-information.update"), {
                    errorBag: "updateProfileInformation",
                    preserveScroll: true,
                    onFinish: () => this.$set(this, "photoPreview", null),
                })
        },

        selectNewPhoto() {
            this.$refs.photo.click()
        },

        updatePhotoPreview() {
            const reader = new FileReader()

            reader.onload = (e) => {
                this.photoPreview = e.target.result
                this.$set(this.user, "profilePhotoUrl", e.target.result)
                this.$nextTick(() => this.$refs.saveChangesButton.$refs.button.focus())
            }

            reader.readAsDataURL(this.$refs.photo.files[0])
        },

        deletePhoto() {
            this.form.delete(route("current-user-photo.destroy"), {
                preserveScroll: true,
                onSuccess: () => (this.photoPreview = null),
            })
        },
    },
}
</script>
